/**
 * PURE options builder for the Jitsi External API (F3-G). Separates the
 * logic (testable without a DOM) from the component that does `new JitsiMeetExternalAPI`.
 *
 * The server resolves the `domain` + `room` (deterministic room with server
 * salt); here only the iframe options are assembled: prejoin OFF,
 * participant displayName, and a conservative config.
 */

/** Input to build the embedded iframe's options. */
export interface JitsiEmbedInput {
  /** Deterministic room resolved by the server. */
  roomName: string;
  /** Participant display name. */
  displayName?: string | null;
  /** Overrides `prejoinPageEnabled` (default: false = no prejoin). */
  prejoin?: boolean;
}

/** Options (without `parentNode`) for `new JitsiMeetExternalAPI(domain, options)`. */
export interface JitsiEmbedOptions {
  roomName: string;
  userInfo?: { displayName: string };
  configOverwrite: {
    prejoinPageEnabled: boolean;
    startWithAudioMuted: boolean;
    disableDeepLinking: boolean;
  };
  interfaceConfigOverwrite: {
    MOBILE_APP_PROMO: boolean;
  };
}

/**
 * Assembles the Jitsi iframe options. Pure: doesn't touch the DOM or the network.
 * `prejoin` off by default (the user already went through the app's pre-join).
 */
export function buildJitsiEmbedOptions(input: JitsiEmbedInput): JitsiEmbedOptions {
  const displayName = input.displayName?.trim();
  return {
    roomName: input.roomName,
    ...(displayName ? { userInfo: { displayName } } : {}),
    configOverwrite: {
      prejoinPageEnabled: input.prejoin ?? false,
      startWithAudioMuted: true,
      disableDeepLinking: true,
    },
    interfaceConfigOverwrite: {
      MOBILE_APP_PROMO: false,
    },
  };
}

/** URL of the External API script for a Jitsi domain (`https://<domain>/external_api.js`). */
export function jitsiExternalApiUrl(domain: string): string {
  const clean = domain
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "");
  return `https://${clean}/external_api.js`;
}
